import { renderRosterDetailPage, type RosterDetailPageSpec } from './roster-detail-page.js';
import { placeInConsolePane } from './roster-page.js';

/**
 * CAOS-557 — the Setup app's SEAT ALLOCATION surface: how many seats this org holds, how many are
 * in use, and which ones can be freed right now — reclaimable seats (deactivated holders, holders
 * who have never signed in) surfaced first, not buried on a person record. Allocating and
 * reclaiming both work on a selection.
 *
 * Authored the SAME way Users/Sharing/Permission Set Groups already are: one authored component
 * (`rosterDetailPage` `seat_allocation`, shipped in `caos-pkg-setup/src/rosterDetailPages/`)
 * interpreted by the generic, app-agnostic renderer in `roster-detail-page.ts`. This module is the
 * only Setup-specific residue: it knows the one component KEY and the `/setup/seats` route — a
 * route-table entry, not a rendering surface.
 *
 * Unlike every prior roster surface in this file's family, Seat Allocation is a SINGLETON — one
 * seat picture per org, not a record among many reached by an id in the URL — so, unlike
 * `setup-users.ts`/`setup-sharing.ts`/`setup-permission-set-groups.ts`, there is no list/detail
 * pair and no `<key>` path segment to parse: `renderRosterDetailPage` is called with no id at all
 * (CAOS-557's SINGLETON-mode addition to that renderer).
 */

export type Navigate = (href: string) => void;

export interface SetupSeatsContext {
  readonly navigate?: Navigate;
}

export interface MetaResponse {
  json(): Promise<unknown>;
}
export type MetaFetch = (path: string) => Promise<MetaResponse>;

const defaultMetaFetch: MetaFetch = (path) =>
  fetch(path, { credentials: 'same-origin' }) as unknown as Promise<MetaResponse>;

let metaFetch: MetaFetch = defaultMetaFetch;

/** Override the component-fetch seam (production wiring / deterministic tests). */
export function setSetupSeatsFetch(fn: MetaFetch): void {
  metaFetch = fn;
}

interface Envelope {
  ok?: boolean;
  data?: unknown;
}

/** GET one authored component by type + key, or `null` on any failure (fail-soft, never a crash). */
async function fetchComponent<T extends { key: string }>(type: string, key: string): Promise<T | null> {
  let env: Envelope;
  try {
    const res = await metaFetch(`/api/metadata/component-get?type=${type}&key=${encodeURIComponent(key)}`);
    env = (await res.json()) as Envelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  const envelope = env.data as { key?: unknown; body?: Record<string, unknown> } | null;
  if (!envelope || typeof envelope.key !== 'string' || !envelope.body) return null;
  return { ...envelope.body, key: envelope.key } as unknown as T;
}

/**
 * Render the Seat Allocation surface from its one authored component. Paints a loading container
 * immediately, fetches the `seat_allocation` spec, then hands off to the generic renderer with no
 * id (SINGLETON mode). Returns `{ root, done }` so a test can await the full (spec + data) round
 * trip before asserting.
 */
export function renderSetupSeats(ctx: SetupSeatsContext): { root: HTMLElement; done: Promise<void> } {
  const root = document.createElement('div');
  root.className = 'setup-pane';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  const done = (async (): Promise<void> => {
    const spec = await fetchComponent<RosterDetailPageSpec>('rosterDetailPage', 'seat_allocation');
    if (!spec) {
      root.replaceChildren();
      const empty = document.createElement('caos-empty-state');
      empty.setAttribute('title', 'Seat Allocation is not available');
      empty.textContent = 'The Seat Allocation page definition could not be read from this org’s installed metadata.';
      root.append(empty);
      return;
    }
    const { root: surface, done: renderDone } = renderRosterDetailPage(spec, undefined, {
      navigate: ctx.navigate,
      fetch: metaFetch,
    });
    placeInConsolePane(root, surface);
    await renderDone;
  })();

  return { root, done };
}
